import { useId, useMemo } from 'react';
import { useSearch } from '@tanstack/react-router';
import { AlignLeftIcon, PencilIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  useWorkspaceFileExistence,
  useWorkspaceFilePreviewLink,
} from '@/features/workspace-files';
import { EyebrowLabel } from '@/shared/components/EyebrowLabel';
import type { MarkdownFilePaths } from '@/shared/components/Markdown';
import { findWorkspacePaths } from '@/shared/components/markdownFilePaths';
import { MarkdownLazy } from '@/shared/components/MarkdownLazy';
import { MarkdownEditor } from '@/shared/components/markdownEditor/MarkdownEditor';
import { DRAWER_PROSE_CLASS } from '../prose';
import { TASK_DESCRIPTION_MAX_LENGTH, toTaskDescription } from '../taskForm';
import { useSetTaskDescription } from '../hooks/useTaskText';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * What a task is about, editable in place.
 *
 * **Commits on an explicit Save, never on blur** — the one place this departs
 * from `TaskTitleField` and from the builder's `CommitField`. Two reasons, both
 * about this field rather than about consistency for its own sake. A
 * description runs to ten thousand characters and several paragraphs, so a blur
 * that wrote silently would be a much larger thing to lose to a stray click
 * than a forty-character name. And this text is handed to the agent on every
 * step the task runs next, so writing it back is a consequence, not a
 * formatting change — a consequence is worth a button press.
 *
 * That also settles the key contract a multiline field cannot inherit: `Enter`
 * belongs to the text, so it cannot mean "save". `Esc` still discards, and
 * still stops propagating so the drawer around it stays open.
 *
 * The draft is owned by the drawer, not by this component. Switching to
 * Comments unmounts this panel, and a draft held here would die on a tab click
 * — the same loss the Save button exists to prevent, arriving by another route.
 *
 * **The way in is the pencil, the same pencil at the same size on the same edge
 * as the title's.** It was a ghost button reading "Edit" while the title above
 * it carried an icon, so one panel offered three fields with three different
 * ways to start changing them — and a reader had to learn which was which
 * before knowing what was editable at all. The commit contracts still differ,
 * and should; the *invitation* had no reason to.
 *
 * The one case that keeps a worded button is a task with no description: there
 * is no text to pencil, and "Add a description" states an act the icon cannot.
 */
export function TaskDescriptionField({
  workspaceId,
  taskId,
  description,
  draft,
  onDraftChange,
}: {
  workspaceId: string;
  taskId: string;
  description: string | null | undefined;
  /** The unsaved text, or `null` when this field is not being edited. */
  draft: string | null;
  onDraftChange: (draft: string | null) => void;
}) {
  const save = useSetTaskDescription();
  const canWork = useCanWork();
  const errorId = useId();
  const fieldId = useId();

  /*
    A workspace path someone wrote in a description is a door to the file
    (spec 140 FR-058), the same door a comment body offers. The paths in the
    text are checked against the workspace — one listing per folder, shared
    with the file browser's cache — and only the ones that are really there
    become links; the rest stay the words as written.

    Which text counts as a path is `markdownFilePaths.ts`'s business and is not
    touched here (FR-058b): passing `filePaths` changes only whether a detected
    path is *linked*.
  */
  const search: { preview?: unknown } = useSearch({ strict: false });
  const previewPath =
    typeof search.preview === 'string' ? search.preview : undefined;
  const previewLink = useWorkspaceFilePreviewLink(workspaceId, previewPath);
  const mentioned = useMemo(
    () => [...new Set(findWorkspacePaths(description ?? ''))],
    [description],
  );
  const existence = useWorkspaceFileExistence(workspaceId, mentioned);
  const filePaths: MarkdownFilePaths = {
    exists: existence.exists,
    linkTo: previewLink,
  };

  const saved = description ?? '';

  const stopEditing = () => {
    onDraftChange(null);
    save.reset();
  };

  if (draft === null) {
    return description ? (
      <section className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between gap-2">
          <EyebrowLabel icon={AlignLeftIcon}>Description</EyebrowLabel>
          {canWork ? (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Edit the description"
              onClick={() => {
                onDraftChange(saved);
              }}
            >
              <PencilIcon />
            </Button>
          ) : null}
        </div>
        {/* Rendered as the markdown it was written in (spec 096 FR-001), and
            still in full — the drawer never truncates a description (063 FR-005). */}
        <MarkdownLazy
          content={description}
          className={DRAWER_PROSE_CLASS}
          filePaths={filePaths}
          breaks
        />
      </section>
    ) : (
      /*
        The sentence stays, and the label stays off it. It says the task was
        *created* without a description, which an empty box would not — and a
        heading over an absence is a heading for nothing.
      */
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-muted-foreground text-sm">
          This task was created without a description.
        </p>
        {canWork ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              onDraftChange('');
            }}
          >
            Add a description
          </Button>
        ) : null}
      </div>
    );
  }

  const tooLong = draft.length > TASK_DESCRIPTION_MAX_LENGTH;
  /*
    Two error kinds, announced differently on purpose. A refusal from the server
    arrives once, after a press, and nothing on screen would otherwise report it
    — so it is a live region. The length message is recomputed on every
    keystroke, and `role="alert"` on that would read the same sentence out again
    on each character typed past the bound. `CommitField` draws the same line.
  */
  const rejection = save.error !== null ? save.error.message : null;
  const lengthError = tooLong
    ? `Description must be ${String(TASK_DESCRIPTION_MAX_LENGTH)} characters or fewer.`
    : null;
  const error = rejection ?? lengthError;
  const unchanged = draft === saved;

  const commit = () => {
    if (save.isPending || unchanged || tooLong) return;
    save.mutate(
      // A blank description is persisted as `null`, never as an empty string —
      // the same rule the create form applies, from the same function.
      { workspaceId, taskId, description: toTaskDescription(draft) },
      { onSuccess: stopEditing },
    );
  };

  return (
    <section className="flex flex-col gap-1.5">
      {/*
        A form label while the field is open, and the eyebrow back once it is
        closed. Eyebrows are how this drawer names things you are *reading*;
        `ui/label` is how the create dialog names things you are *filling in*.
        Both were already true and neither was stated, so the same three fields
        wore two labelling languages depending on which surface you met them on.
      */}
      <Label htmlFor={fieldId}>Description</Label>
      {/* Written as markdown with a toolbar and a preview (spec 145), the
          preview in the drawer's own prose so it reads as the saved text will. */}
      <MarkdownEditor
        id={fieldId}
        autoFocus
        rows={6}
        aria-invalid={error !== null}
        aria-describedby={error !== null ? errorId : undefined}
        value={draft}
        disabled={save.isPending}
        previewClassName={DRAWER_PROSE_CLASS}
        onValueChange={(value) => {
          onDraftChange(value);
          save.reset();
        }}
        onKeyDown={(event) => {
          // Enter belongs to the text here. Only Esc is claimed, and it is
          // stopped so it discards the draft without also closing the drawer.
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            stopEditing();
          }
        }}
      />
      {error !== null && (
        <span
          id={errorId}
          role={rejection !== null ? 'alert' : undefined}
          className="text-destructive text-xs"
        >
          {error}
        </span>
      )}
      {/*
        Said while the field is open, and only then. A reader who is not
        changing anything is not owed a warning; someone typing into it is
        owed the consequence.
      */}
      <p className="text-muted-foreground text-xs">
        This is given to the agent on each step this task runs next. Steps
        already run keep the text they were given.
      </p>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={save.isPending || unchanged || tooLong}
          onClick={commit}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={save.isPending}
          onClick={stopEditing}
        >
          Cancel
        </Button>
      </div>
    </section>
  );
}
